import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';

export default function FiberTerminationPage() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const connectorTypes = [
    { name: 'SC Konnektör', desc: 'Push-pull mekanizmalı, en yaygın kullanılan konnektör tipi', icon: 'ri-plug-line' },
    { name: 'LC Konnektör', desc: 'Küçük form faktör, yüksek yoğunluklu uygulamalar için ideal', icon: 'ri-plug-2-line' },
    { name: 'ST Konnektör', desc: 'Bajonet kilitleme, eski sistemlerde yaygın kullanım', icon: 'ri-settings-3-line' },
    { name: 'FC Konnektör', desc: 'Vida kilitleme, yüksek hassasiyet gereken uygulamalar', icon: 'ri-settings-4-line' },
  ];

  const fiberTypes = [
    { type: 'OS2 (9/125)', desc: 'Singlemode fiber, uzun mesafe uygulamaları', color: 'yellow' },
    { type: 'OM3 (50/125)', desc: 'Multimode fiber, 10G Ethernet için optimize', color: 'cyan' },
    { type: 'OM4 (50/125)', desc: 'Multimode fiber, 40G/100G uygulamaları', color: 'violet' },
    { type: 'OM5 (50/125)', desc: 'Multimode fiber, SWDM uygulamaları', color: 'lime' },
  ];

  const services = [
    {
      title: 'Pigtail Ek İşlemi',
      desc: 'Fiber kablonun pigtail ile fusion splicing yöntemi ile eklenmesi',
      features: ['Fusion Splicing', 'Düşük Kayıp', 'Kalıcı Bağlantı', 'OTDR Test'],
    },
    {
      title: 'Patch Panel Kurulumu',
      desc: 'Fiber patch panel montajı ve konnektör sonlandırma işlemleri',
      features: ['Panel Montaj', 'Kablo Yönetimi', 'Etiketleme', 'Test ve Devreye Alma'],
    },
    {
      title: 'ODF Kurulumu',
      desc: 'Optical Distribution Frame kurulumu ve fiber dağıtım organizasyonu',
      features: ['ODF Montaj', 'Fiber Yönetimi', 'Splice Tray', 'Dokümantasyon'],
    },
    {
      title: 'Konnektör Montajı',
      desc: 'SC, LC, ST, FC konnektör montajı ve polisaj işlemleri',
      features: ['Epoxy Yöntemi', 'Anaerobic Yöntemi', 'Polisaj', 'Kalite Kontrol'],
    },
  ];

  const faqData = [
    {
      question: 'Fiber sonlandırma nedir ve neden önemlidir?',
      answer: 'Fiber sonlandırma, fiber optik kablonun ucuna konnektör takılması işlemidir. Bu işlem, fiber kabloların aktif ekipmanlara, patch panellere veya diğer fiber kablolara bağlanabilmesi için gereklidir. Doğru yapılmayan sonlandırma işlemi yüksek kayıplara ve sinyal kalitesinde düşüşe neden olur.',
    },
    {
      question: 'SC ve LC konnektör arasındaki fark nedir?',
      answer: 'SC konnektör daha büyük boyutlu, push-pull mekanizmalı ve kolay kullanımlıdır. LC konnektör ise küçük form faktörlü (SFF) olup, yüksek yoğunluklu uygulamalarda tercih edilir. LC konnektör, aynı alana daha fazla port yerleştirme imkanı sağlar.',
    },
    {
      question: 'Pigtail ek ile konnektör montajı arasındaki fark nedir?',
      answer: 'Pigtail ek yönteminde, fabrikada üretilmiş kaliteli konnektörlü pigtail, fiber kabloya fusion splicing ile eklenir. Bu yöntem daha düşük kayıp ve daha yüksek kalite sağlar. Konnektör montajında ise konnektör doğrudan fiber ucuna takılır, daha hızlıdır ancak kayıp değerleri daha yüksek olabilir.',
    },
    {
      question: 'Fiber sonlandırma işlemi ne kadar sürer?',
      answer: 'Standart bir fiber sonlandırma işlemi, fiber sayısına bağlı olarak değişir. 12 fiber için ortalama 2-3 saat, 24 fiber için 4-6 saat sürmektedir. Pigtail ek yöntemi kullanıldığında süre biraz daha uzun olabilir ancak kalite daha yüksektir.',
    },
    {
      question: 'Hangi fiber tiplerine sonlandırma yapıyorsunuz?',
      answer: 'Singlemode (OS1, OS2) ve multimode (OM1, OM2, OM3, OM4, OM5) tüm fiber tiplerine profesyonel sonlandırma hizmeti veriyoruz. Ayrıca özel fiber tipler (bend-insensitive, armored) için de sonlandırma yapabiliyoruz.',
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <title>Fiber Sonlandırma ve Ek Hizmeti İstanbul | SC LC Konnektör Montajı</title>
      <meta name="description" content="İstanbul ve çevre illerde profesyonel fiber optik sonlandırma hizmeti. SC, LC, ST konnektör montajı, pigtail ek, patch panel ve ODF kurulumu. OM3, OM4, OS2 fiber sonlandırma." />
      <meta name="keywords" content="fiber sonlandırma, fiber ek, SC konnektör, LC konnektör, pigtail ek, patch panel, ODF kurulum, OM3 OM4 fiber, fiber konnektör montajı, İstanbul fiber sonlandırma" />
      <link rel="canonical" href="https://fiberek.com/fiber-optik-hizmetleri/fiber-sonlandirma" />
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      <div className="min-h-screen bg-dark-950">
        <Navbar />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 via-dark-950 to-blue-900/20"></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                  Fiber Sonlandırma
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                    ve Ek Hizmeti
                  </span>
                </h1>

                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  SC, LC, ST, FC konnektör sonlandırma işlemleri. OM3, OM4, OS2 fiber tiplerine uygun profesyonel ek hizmeti. 
                  Pigtail ek, patch panel ve ODF kurulumu.
                </p>

                <div className="flex flex-wrap gap-4">
                  <a
                    href="tel:+905537165561"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-all duration-300 font-semibold shadow-lg shadow-cyan-500/30 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line"></i>
                    Hemen Ara
                  </a>
                  <a
                    href="https://wa.me/905537165561"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-300 font-semibold shadow-lg shadow-emerald-500/30 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line"></i>
                    WhatsApp
                  </a>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="relative w-full h-96 rounded-2xl overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/cb79b4550044667cb344e4027cc66872.jpg"
                    alt="Fiber Sonlandırma"
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Connector Types */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Konnektör Tipleri</h2>
              <p className="text-gray-400">Tüm standart fiber optik konnektör tiplerine sonlandırma hizmeti</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {connectorTypes.map((connector, index) => (
                <motion.div
                  key={connector.name}
                  ref={ref}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-6 border border-white/10 hover:border-cyan-500/50 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${connector.icon} text-2xl text-cyan-400`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{connector.name}</h3>
                  <p className="text-gray-400 text-sm">{connector.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Fiber Types */}
        <section className="py-20 bg-gradient-to-br from-cyan-900/10 via-dark-950 to-blue-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Desteklenen Fiber Tipleri</h2>
              <p className="text-gray-400">Singlemode ve multimode tüm fiber tiplerine profesyonel sonlandırma</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {fiberTypes.map((fiber, index) => (
                <motion.div
                  key={fiber.type}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-dark-900 rounded-xl p-6 border border-${fiber.color}-500/30 hover:border-${fiber.color}-500 transition-all duration-300`}
                >
                  <div className={`text-2xl font-bold text-${fiber.color}-400 mb-2`}>{fiber.type}</div>
                  <p className="text-gray-400 text-sm">{fiber.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Sonlandırma Hizmetlerimiz</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-8 border border-white/10"
                >
                  <h3 className="text-2xl font-bold text-white mb-3">{service.title}</h3>
                  <p className="text-gray-400 mb-6">{service.desc}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-gray-300">
                        <i className="ri-check-line text-cyan-400"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20 bg-gradient-to-br from-cyan-900/10 via-dark-950 to-blue-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  Fiber Sonlandırma Yöntemleri
                </h2>
                <div className="prose prose-invert max-w-none space-y-6">
                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-2xl font-bold text-white mb-3">Pigtail Ek Yöntemi</h3>
                    <p className="text-gray-300 leading-relaxed mb-4">
                      Pigtail ek yöntemi, fiber sonlandırmada en kaliteli sonucu veren yöntemdir. Bu yöntemde, 
                      fabrikada üretilmiş ve test edilmiş konnektörlü pigtail, fiber kabloya fusion splicing ile eklenir.
                    </p>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-cyan-400 mt-1"></i>
                        <span>Çok düşük kayıp değerleri (0.05-0.15 dB)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-cyan-400 mt-1"></i>
                        <span>Fabrika kalitesinde konnektör</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-cyan-400 mt-1"></i>
                        <span>Uzun ömürlü ve güvenilir</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-cyan-400 mt-1"></i>
                        <span>Kritik uygulamalar için ideal</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-2xl font-bold text-white mb-3">Direkt Konnektör Montajı</h3>
                    <p className="text-gray-300 leading-relaxed mb-4">
                      Konnektörün doğrudan fiber ucuna takılması yöntemidir. Epoxy veya anaerobic yapıştırıcı 
                      kullanılarak fiber, konnektör içine sabitlenir ve polisaj işlemi yapılır.
                    </p>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-cyan-400 mt-1"></i>
                        <span>Daha hızlı uygulama</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-cyan-400 mt-1"></i>
                        <span>Daha ekonomik</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-cyan-400 mt-1"></i>
                        <span>Saha koşullarında uygulanabilir</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-cyan-400 mt-1"></i>
                        <span>Standart uygulamalar için yeterli</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  Patch Panel ve ODF Kurulumu
                </h2>
                <div className="space-y-6">
                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">Fiber Patch Panel</h3>
                    <p className="text-gray-300 leading-relaxed mb-4">
                      Fiber patch panel, fiber kabloların organize edilmesi ve yönetilmesi için kullanılan pasif ekipmandır. 
                      Arka taraftan gelen fiber kablolar sonlandırılır, ön taraftan patch cord ile bağlantı sağlanır.
                    </p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-cyan-400 mt-1"></i>
                        <span>12, 24, 48, 96 port kapasiteler</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-cyan-400 mt-1"></i>
                        <span>SC, LC, ST adaptör seçenekleri</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-cyan-400 mt-1"></i>
                        <span>Kablo yönetimi ve etiketleme</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-cyan-400 mt-1"></i>
                        <span>19" rack montaj uyumlu</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">ODF (Optical Distribution Frame)</h3>
                    <p className="text-gray-300 leading-relaxed mb-4">
                      ODF, büyük kapasiteli fiber dağıtım uygulamaları için kullanılır. Splice tray'ler ile fiber ekleme, 
                      adaptör panelleri ile sonlandırma ve patch cord yönetimi tek bir çözümde birleşir.
                    </p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-cyan-400 mt-1"></i>
                        <span>144, 288, 576 fiber kapasiteler</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-cyan-400 mt-1"></i>
                        <span>Splice tray ve adaptör paneli</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-cyan-400 mt-1"></i>
                        <span>Kablo giriş ve yönetim sistemi</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-cyan-400 mt-1"></i>
                        <span>Duvar veya rack montaj</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">Kurulum Süreci</h3>
                    <ol className="space-y-3 text-gray-300 text-sm">
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center text-cyan-400 text-xs font-bold">1</span>
                        <span>Panel montajı ve kablo girişi</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center text-cyan-400 text-xs font-bold">2</span>
                        <span>Fiber sonlandırma veya pigtail ek</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center text-cyan-400 text-xs font-bold">3</span>
                        <span>Kablo yönetimi ve organizasyon</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center text-cyan-400 text-xs font-bold">4</span>
                        <span>Etiketleme ve dokümantasyon</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center text-cyan-400 text-xs font-bold">5</span>
                        <span>Test ve devreye alma</span>
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Sıkça Sorulan Sorular
              </h2>
            </div>

            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <motion.details
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-dark-900 rounded-xl border border-white/10 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <h3 className="text-lg font-semibold text-white pr-4">{faq.question}</h3>
                    <i className="ri-arrow-down-s-line text-2xl text-cyan-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-6 pb-6">
                    <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                  </div>
                </motion.details>
              ))}
            </div>
          </div>
        </section>

        {/* Blog & Rehberler Section */}
        <section className="py-20 bg-gradient-to-br from-cyan-900/10 via-dark-950 to-blue-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Blog & Rehberler</h2>
              <p className="text-gray-400">Fiber optik teknolojileri hakkında detaylı bilgiler</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=fiber%20optic%20cable%20termination%20with%20SC%20LC%20connectors%2C%20professional%20technician%20installing%20fiber%20patch%20panel%2C%20colorful%20fiber%20cables%20organized%20neatly%2C%20modern%20data%20center%20environment%20with%20clean%20white%20background&width=400&height=250&seq=termination-blog1&orientation=landscape"
                    alt="Fusion Splicing"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-cyan-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">Fusion Splicing Nedir? Nasıl Yapılır?</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Fiber optik kabloların kaynaştırılarak birleştirilmesi işlemi olan fusion splicing tekniği ve uygulama detayları.</p>
                  <a
                    href="/blog/fusion-splicing-nedir"
                    className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>

              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=fiber%20optic%20cable%20repair%20fusion%20splicing%20process%2C%20technician%20working%20with%20precision%20equipment%2C%20blue%20glowing%20fiber%20cables%2C%20professional%20technical%20environment%20with%20modern%20splicing%20tools&width=400&height=250&seq=termination-blog2&orientation=landscape"
                    alt="Fiber Kablo Tamiri"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-cyan-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">Fiber Optik Kablo Tamiri Nasıl Yapılır?</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Kesilen veya hasar gören fiber optik kabloların fusion splicing yöntemi ile profesyonel onarım teknikleri.</p>
                  <a
                    href="/blog/fiber-optik-kablo-tamiri-nasil-yapilir"
                    className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>

              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20building%20fiber%20optic%20infrastructure%20installation%2C%20technician%20installing%20fiber%20cables%20in%20vertical%20riser%20shaft%2C%20organized%20fiber%20patch%20panels%20in%20building%20telecom%20room&width=400&height=250&seq=termination-blog3&orientation=landscape"
                    alt="Bina Fiber Altyapı"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-cyan-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">Fabrikalarda Fiber Altyapı Rehberi</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Endüstriyel tesislerde fiber optik altyapı planlama, kurulum ve yönetim süreçleri hakkında kapsamlı rehber.</p>
                  <a
                    href="/blog/fabrikalarda-fiber-altyapi-rehberi"
                    className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-br from-cyan-900/20 via-dark-950 to-blue-900/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Fiber Sonlandırma Teklifi Alın
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Projeniz için ücretsiz keşif ve detaylı teklif
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="tel:+905537165561"
                className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-all duration-300 font-semibold text-lg shadow-lg shadow-cyan-500/30 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                +90 553 716 55 61
              </a>
              <a
                href="https://wa.me/905537165561"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-300 font-semibold text-lg shadow-lg shadow-emerald-500/30 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </section>

        <Footer />
        <FloatingButtons />
      </div>
    </>
  );
}